import { createHttpLink, from, InMemoryCache } from '@apollo/client/core';
import { ApolloClient } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { DefaultApolloClient, ApolloClients, provideApolloClients } from '@vue/apollo-composable';
import { createApolloProvider } from '@vue/apollo-option';

import { useMerchantStore } from '~/store/merchant';
import { useAuthStore } from '~/store/auth';
import { useImpersonationStore } from '~/store/impersonation';
import { IMPERSONATION_REQUEST_HEADER } from '@constants';

/* eslint-disable @typescript-eslint/ban-ts-comment */

export default defineNuxtPlugin(nuxtApp => {
  const clients: { [key: string]: ApolloClient<any> } = {};
  const { apollo } = nuxtApp.$config.public;
  const store = useAuthStore();

  for (const [key, clientConfig] of Object.entries(apollo.clients)) {
    const errorLink = onError(({ graphQLErrors, networkError }) => {
      if (graphQLErrors) {
        graphQLErrors.map(({ message, locations, path }) => {
          console.error(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);
        });
      }

      if (networkError) {
        console.error(`[Network error]: ${networkError}`);
      }
    });

    const authLink = setContext((_, { headers }) => {
      const { selectedStoreIds } = storeToRefs(useMerchantStore());
      // Read at request time, not plugin-construction time: this plugin runs before init.ts has
      // restored the session, and the token is replaced again on every refresh.
      const accessToken = store.accessToken;
      // Send the impersonation cookie only while a session is active, matching the REST interceptor: normal
      // GraphQL traffic stays uncredentialed so a missing CORS allowlist cannot break the app.
      const credentials = useImpersonationStore().shouldSendCredentials ? 'include' : 'same-origin';
      // Derived from `credentials` so the two can never diverge: the gateway grants credentialed CORS only to
      // requests advertising this header on the preflight.
      const impersonationHeaders = credentials === 'include' ? { [IMPERSONATION_REQUEST_HEADER]: '1' } : {};

      if (!accessToken) {
        return {
          credentials,
          headers: {
            ...headers,
            ...impersonationHeaders,
          },
        };
      }

      return {
        credentials,
        headers: {
          ...headers,
          ...impersonationHeaders,
          authorization: `Bearer ${accessToken}`,
          'x-store-id': selectedStoreIds.value,
        },
      };
    });

    const httpLink = createHttpLink({
      uri: clientConfig.httpEndpoint,
      useGETForQueries: false,
    });

    clients[key] = new ApolloClient({
      cache: new InMemoryCache(),
      link: from([errorLink, authLink, httpLink]),
      ssrForceFetchDelay: 100,
    });
  }

  const defaultClient = clients?.default;

  provideApolloClients(clients);
  nuxtApp.vueApp.provide(ApolloClients, clients);
  nuxtApp.vueApp.provide(DefaultApolloClient, clients.default);
  nuxtApp.vueApp.use(createApolloProvider({ defaultClient }) as any);
  nuxtApp._apolloClients = clients;

  return {
    provide: {
      apollo: { clients, defaultClient },
    },
  };
});
